/**
 * features/landing/LandingIndex.tsx
 * ------------------------------------------------------------------
 * Landing variant C — the editorial index. Preview at /?v=c.
 *
 * A different answer to the same problem. The live page is 15 screens and half
 * of it is ten full-height case studies; the School Portal band looks like an
 * intrusion partly because it is a coloured block wedged in front of that wall.
 * This version removes the wall rather than recolouring the block.
 *
 * Three rules it keeps to:
 *   1. The page is near-monochrome. Every colour on it comes from a product's
 *      own screenshot or its one accent dot, so no section can clash with
 *      another — School Portal included. That is the whole fix.
 *   2. Work is ONE list, not ten sections. A row shows the name, what it is and
 *      who it is for; opening a row reveals the detail and the screenshot. The
 *      reader chooses the depth instead of scrolling past it.
 *   3. Type does the work the glows and the dark hero did. Nothing animates in
 *      except a fade, and that is dropped for reduced-motion.
 *
 * It shares work.ts, BrowserFrame and the enquiry route with the other
 * variants; only the layout is its own.
 * ------------------------------------------------------------------
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRight, Minus, Plus } from 'lucide-react';
import AcepsleWordmark from '@/components/branding/AcepsleWordmark';
import { BrowserFrame } from '@/features/landing/BrowserFrame';
import { useSiteImages } from '@/features/media/useSiteImages';
import { FRAME_CSS } from '@/features/landing/frameCss';
import { INDEX_CSS } from '@/features/landing/landingIndexCss';
import {
  APPROACH,
  COMPANY,
  PROJECTS,
  SCHOOL_PORTAL,
  SERVICES,
  type Project,
} from '@/features/landing/work';

const ENQUIRE = '?enquire';

type Filter = 'all' | 'product' | 'client';

/*
 * Palettes. Signature is the page; Night is what a reader in dark mode gets,
 * applied by prefers-color-scheme in CSS. The others stay reachable at ?c= for
 * showing a client an alternative, but nothing on the page offers them — the
 * preview switcher came out when Signature was chosen.
 */
type Palette = 'signature' | 'studio' | 'night' | 'blueprint';

/*
 * How much orange, and where. Three takes on the same layout, all of them
 * carrying back the grid from the previous page:
 *   warm  — warm paper, an orange grid behind the hero, orange in the type
 *   band  — orange as a surface: two solid orange bands carry the page
 *   bold  — the old charcoal hero with its orange grid and glows, over the index
 * No class is the plain version that is live now.
 */
type Shade = 'warm' | 'band' | 'bold';

const SHADES: Array<{ id: Shade; label: string; swatch: string }> = [
  { id: 'warm', label: 'Warm — orange in the type and the grid', swatch: 'linear-gradient(135deg,#FFFBF7 0 50%,#FF6B35 50%)' },
  { id: 'band', label: 'Banded — orange as whole surfaces', swatch: 'linear-gradient(135deg,#FF6B35 0 50%,#FFFFFF 50%)' },
  { id: 'bold', label: 'Bold — charcoal hero, orange grid and glow', swatch: 'linear-gradient(135deg,#1A1C20 0 50%,#FF6B35 50%)' },
];

/*
 * Banded is the page, chosen 2026-10-02. The others stay reachable for
 * comparison and as a way back — ?s=warm, ?s=bold, and ?s=plain for the
 * version with no orange surfaces at all.
 */
function initialShade(): Shade | null {
  if (typeof window === 'undefined') return 'band';
  const s = new URLSearchParams(window.location.search).get('s');
  if (s === 'plain') return null;
  return SHADES.some((x) => x.id === s) ? (s as Shade) : 'band';
}

const PALETTES: Array<{ id: Palette; label: string; paper: string; accent: string }> = [
  { id: 'signature', label: 'Signature — white, Acepsle orange', paper: '#FFFFFF', accent: '#FF6B35' },
  { id: 'studio', label: 'Studio — warm paper, deep green', paper: '#FAF8F3', accent: '#1F7A4D' },
  { id: 'night', label: 'Night — inverted, orange', paper: '#0E1013', accent: '#FF6B35' },
  { id: 'blueprint', label: 'Blueprint — cool white, technical blue', paper: '#FBFCFE', accent: '#1D4ED8' },
];

function initialPalette(): Palette | null {
  if (typeof window === 'undefined') return null;
  const c = new URLSearchParams(window.location.search).get('c');
  return PALETTES.some((p) => p.id === c) ? (c as Palette) : null;
}

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'all', label: 'Everything' },
  { id: 'product', label: 'Our products' },
  { id: 'client', label: 'Built for clients' },
];

/** One row of the index. Closed it is three facts; open it is the case study. */
function Row({
  project,
  src,
  open,
  onToggle,
}: {
  project: Project;
  src?: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={`ix-row${open ? ' open' : ''}`}>
      <button className="ix-head" onClick={onToggle} aria-expanded={open}>
        <span className="ix-dot" style={{ background: project.accent }} />
        <span className="ix-name">{project.name}</span>
        <span className="ix-what">{project.summary}</span>
        <span className="ix-kind">{project.kind === 'client' ? 'Client' : 'Product'}</span>
        <span className="ix-sign">{open ? <Minus size={16} /> : <Plus size={16} />}</span>
      </button>

      {open && (
        <div className="ix-detail">
          <div>
            <p className="ix-body">{project.body}</p>
            <div className="ix-chips">
              {project.chips.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </div>
            {project.href && (
              <a className="ix-visit" href={project.href} target="_blank" rel="noreferrer">
                {project.linkLabel ?? `Visit ${project.domain}`} <ArrowUpRight size={14} />
              </a>
            )}
          </div>
          <BrowserFrame
            image={project.image}
            src={src}
            domain={project.domain}
            accent={project.accent}
            alt={`${project.name} — ${project.summary}`}
          />
        </div>
      )}
    </div>
  );
}

export function LandingIndex({ onSignIn }: { onSignIn: () => void }) {
  const { images } = useSiteImages();
  const [filter, setFilter] = useState<Filter>('all');
  /*
   * null means "follow the reader": Signature in light, Night in dark, decided
   * by prefers-color-scheme in CSS rather than here, so it also follows a
   * reader who changes their system setting with the page already open.
   * Choosing a palette in the preview switcher pins it.
   */
  const [palette] = useState<Palette | null>(initialPalette);
  const [shade] = useState<Shade | null>(initialShade);
  const [revealed, setRevealed] = useState(false);

  /* The split-in-half intro, as on the main page. Skipped for reduced motion. */
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setRevealed(true);
      return;
    }
    const t = window.setTimeout(() => setRevealed(true), 1500);
    return () => window.clearTimeout(t);
  }, []);
  const [openId, setOpenId] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  const rows = useMemo(
    () => (filter === 'all' ? PROJECTS : PROJECTS.filter((p) => p.kind === filter)),
    [filter],
  );

  /* A single fade-up, and only where motion is welcome. */
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.querySelectorAll('.ix-rise').forEach((n) => n.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px' },
    );
    el.querySelectorAll('.ix-rise').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  return (
    <div
      className={`ix ${palette ? `p-${palette}` : 'auto'}${shade ? ` s-${shade}` : ''}${
        revealed ? ' ix--in' : ''
      }`}
      ref={root}
    >
      <style>{INDEX_CSS + FRAME_CSS}</style>

      {/* Intro curtain — the page splitting in half */}
      <div className="ix-intro" aria-hidden="true">
        <div className="ix-intro-panel l" />
        <div className="ix-intro-panel r" />
        <div className="ix-intro-mark">
          <div className="ix-intro-inner">
            <AcepsleWordmark height={58} variant="reversed" withTagline />
          </div>
        </div>
      </div>

      <header className="ix-nav">
        <div className="ix-wrap ix-nav-row">
          <a className="ix-logo" href="#top">
            {COMPANY.displayName}
            <span>.</span>
          </a>
          <nav>
            <a href="#work">Work</a>
            <a href="#services">Services</a>
            <a href="#how">How we work</a>
          </nav>
          <div className="ix-nav-cta">
            <button onClick={onSignIn}>Sign in</button>
            <a className="ix-btn" href={ENQUIRE}>
              Start a project
            </a>
          </div>
        </div>
      </header>

      {/* Hero. The grid and the glow are painted by the chosen style; with no
          style class the layer renders nothing, which is the plain version. */}
      <section className="ix-hero" id="top">
        <span className="ix-bg" aria-hidden="true">
          <span className="ix-grid" />
          <span className="ix-glow a" />
          <span className="ix-glow b" />
        </span>
        <div className="ix-wrap">
          <p className="ix-kicker ix-enter e1">
            {COMPANY.tradingName} · {COMPANY.tagline}
          </p>
          <h1 className="ix-enter e2">
            We build the software
            <br />
            organisations <em>actually run on.</em>
          </h1>
          <p className="ix-lead ix-enter e3">
            Ten platforms, designed and built in-house: school management, finance and
            compliance, booking and retail, and our own consumer products. All of them
            live. All of them maintained by the people who wrote them.
          </p>
          <div className="ix-hero-cta ix-enter e4">
            <a className="ix-btn lg" href={ENQUIRE}>
              Start a project
            </a>
            <a className="ix-btn lg line" href="#work">
              See the work
            </a>
          </div>
        </div>
      </section>

      {/* The flagship gets a line, not a band — its colour stays in its own page. */}
      <section className="ix-flag ix-rise">
        <div className="ix-wrap ix-flag-row">
          <div>
            <p className="ix-flag-k">Our flagship</p>
            <h2>
              {SCHOOL_PORTAL.name} — <em>{SCHOOL_PORTAL.tagline.replace(/\.$/, '')}</em>
            </h2>
            <p className="ix-flag-sum">{SCHOOL_PORTAL.scope} Running today in two schools.</p>
          </div>
          <a className="ix-btn" href={SCHOOL_PORTAL.pitchHref}>
            {SCHOOL_PORTAL.pitchLabel} <ArrowUpRight size={14} />
          </a>
        </div>
      </section>

      {/* Work — one index, filtered, each row opening to its own case study. */}
      <section className="ix-work" id="work">
        <div className="ix-wrap">
          <div className="ix-sec-head ix-rise">
            <h2>The work</h2>
            <div className="ix-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  className={filter === f.id ? 'on' : ''}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                  <span>
                    {f.id === 'all'
                      ? PROJECTS.length
                      : PROJECTS.filter((p) => p.kind === f.id).length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="ix-list ix-rise">
            {rows.map((p) => (
              <Row
                key={p.id}
                project={p}
                src={images[p.id]?.url}
                open={openId === p.id}
                onToggle={() => setOpenId(openId === p.id ? null : p.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Services and method, as two short lists rather than two bands. */}
      <section className="ix-two" id="services">
        <div className="ix-wrap ix-two-grid">
          <div className="ix-rise">
            <h2>What we do</h2>
            <ul className="ix-plain">
              {SERVICES.map((s) => (
                <li key={s.title}>
                  <b>{s.title}</b>
                  <span>{s.body}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="ix-rise" id="how">
            <h2>How we work</h2>
            <ol className="ix-steps">
              {APPROACH.map((s, i) => (
                <li key={s.title}>
                  <span className="ix-n">{String(i + 1).padStart(2, '0')}</span>
                  <b>{s.title}</b>
                  <span>{s.body}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="ix-end" id="contact">
        <div className="ix-wrap">
          <h2 className="ix-rise">
            Tell us what your organisation
            <br />
            is still doing by hand.
          </h2>
          <div className="ix-end-cta ix-rise">
            <a className="ix-btn lg" href={ENQUIRE}>
              Start a project
            </a>
            <a className="ix-btn lg line" href={`mailto:${COMPANY.email}`}>
              {COMPANY.email}
            </a>
          </div>
          <p className="ix-foot ix-rise">
            {COMPANY.legalName} · {COMPANY.address} · {COMPANY.phone}
            <br />
            <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a> ·{' '}
            <a href="/data-deletion">Data deletion</a>
          </p>
        </div>
      </section>
    </div>
  );
}
